import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { importAssets } from '../../../../models/assetsModel';
import Modal from '../../../../components/common/Modal';
import Button from '../../../../components/common/Button';

function ImportAssetsModal({ isOpen, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Please select a valid Excel (.xlsx, .xls) or CSV file');
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please select a file to import');
      return;
    }

    try {
      setIsUploading(true);
      const result = await importAssets(file);
      
      if (result) {
        toast.success(`Successfully imported ${result.count || 0} assets`);
        onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error('Error importing assets:', error);
      toast.error('Failed to import assets');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Assets">
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-blue-800 mb-2">Import Instructions</h3>
          <div className="text-sm text-blue-700 space-y-2">
            <p>• Upload an Excel (.xlsx, .xls) or CSV file containing asset data</p>
            <p>• Ensure your file has the correct column headers</p>
            <p>• Required columns: asset_name, asset_category_id</p>
            <p>• Optional columns: asset_description, asset_cost, asset_vendor_id, etc.</p>
          </div>
        </div>

        {/* File Upload */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Asset File *
            </label>
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                  </svg>
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">Excel or CSV files only</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".xlsx,.xls,.csv"
                  required
                />
              </label>
            </div>
            
            {file && (
              <div className="mt-2 text-sm text-gray-600">
                Selected file: {file.name}
              </div>
            )}
          </div>

          {/* Sample Template */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-800 mb-2">Sample Template</h4>
            <div className="text-xs text-gray-600 space-y-1">
              <div className="font-mono bg-white p-2 rounded border">
                asset_name | asset_category_id | asset_description | asset_cost | asset_vendor_id
              </div>
              <div className="font-mono bg-white p-2 rounded border">
                Laptop Dell XPS | 1 | High-performance laptop | 15000.00 | 2
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUploading || !file}
            >
              {isUploading ? 'Importing...' : 'Import Assets'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default ImportAssetsModal;
